/**
 * React Query hooks for Service Clusters (UI Redesign).
 *
 * Mutations invalidate both the cluster list and the exhaustion report, since
 * approving or spending a keyword can flip a cluster's availability.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  serviceClusterQueryKeys,
  serviceClusterService,
  type ClusterSource,
  type ClusterStatus,
  type KeywordStatus,
} from '@/services/serviceClusterService';

export function useServiceClusters(tenantId: string, includeArchived = false) {
  return useQuery({
    queryKey: serviceClusterQueryKeys.list(tenantId, includeArchived),
    queryFn: () => serviceClusterService.list(tenantId, includeArchived),
    enabled: !!tenantId,
    staleTime: 30_000,
  });
}

export function useServiceCluster(tenantId: string, clusterId: string | null) {
  return useQuery({
    queryKey: serviceClusterQueryKeys.detail(clusterId),
    queryFn: () => {
      if (!clusterId) throw new Error('clusterId required');
      return serviceClusterService.get(tenantId, clusterId);
    },
    enabled: !!tenantId && !!clusterId,
    staleTime: 15_000,
  });
}

export function useClusterExhaustion(tenantId: string) {
  return useQuery({
    queryKey: serviceClusterQueryKeys.exhaustion(tenantId),
    queryFn: () => serviceClusterService.exhaustion(tenantId),
    enabled: !!tenantId,
    // Polled so the "you have run out" banner appears without a manual reload.
    // Backend logs this endpoint at debug() precisely because it repeats.
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

/** Invalidate everything a keyword/cluster change can affect. */
function useClusterInvalidation(tenantId: string) {
  const queryClient = useQueryClient();
  return (clusterId?: string) => {
    queryClient.invalidateQueries({ queryKey: serviceClusterQueryKeys.list(tenantId, false) });
    queryClient.invalidateQueries({ queryKey: serviceClusterQueryKeys.list(tenantId, true) });
    queryClient.invalidateQueries({ queryKey: serviceClusterQueryKeys.exhaustion(tenantId) });
    if (clusterId) {
      queryClient.invalidateQueries({ queryKey: serviceClusterQueryKeys.detail(clusterId) });
    }
  };
}

export function useCreateCluster(tenantId: string) {
  const invalidate = useClusterInvalidation(tenantId);
  return useMutation({
    mutationFn: (payload: { name: string; description?: string; source?: ClusterSource }) =>
      serviceClusterService.create(tenantId, payload),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateCluster(tenantId: string) {
  const invalidate = useClusterInvalidation(tenantId);
  return useMutation({
    mutationFn: ({
      clusterId,
      ...payload
    }: {
      clusterId: string;
      name?: string;
      description?: string;
      status?: ClusterStatus;
    }) => serviceClusterService.update(tenantId, clusterId, payload),
    onSuccess: (_data, vars) => invalidate(vars.clusterId),
  });
}

export function useDeleteCluster(tenantId: string) {
  const invalidate = useClusterInvalidation(tenantId);
  return useMutation({
    mutationFn: (clusterId: string) => serviceClusterService.remove(tenantId, clusterId),
    onSuccess: () => invalidate(),
  });
}

export function useSeedClustersFromBrandBible(tenantId: string) {
  const invalidate = useClusterInvalidation(tenantId);
  return useMutation({
    mutationFn: () => serviceClusterService.seedFromBrandBible(tenantId),
    onSuccess: () => invalidate(),
  });
}

export function useSuggestClusterKeywords(tenantId: string) {
  const invalidate = useClusterInvalidation(tenantId);
  return useMutation({
    mutationFn: ({ clusterId, limit }: { clusterId: string; limit?: number }) =>
      serviceClusterService.suggest(tenantId, clusterId, limit),
    onSuccess: (_data, vars) => invalidate(vars.clusterId),
  });
}

export function useAddClusterKeywords(tenantId: string) {
  const invalidate = useClusterInvalidation(tenantId);
  return useMutation({
    mutationFn: ({
      clusterId,
      keywords,
      status,
    }: {
      clusterId: string;
      keywords: string[];
      status?: KeywordStatus;
    }) => serviceClusterService.addKeywords(tenantId, clusterId, keywords, status),
    onSuccess: (_data, vars) => invalidate(vars.clusterId),
  });
}

export function useSetKeywordStatus(tenantId: string) {
  const invalidate = useClusterInvalidation(tenantId);
  return useMutation({
    mutationFn: ({
      clusterId,
      keywordIds,
      status,
      reason,
    }: {
      clusterId: string;
      keywordIds: string[];
      status: KeywordStatus;
      reason?: string | null;
    }) =>
      serviceClusterService.setKeywordStatus(tenantId, clusterId, keywordIds, status, reason),
    onSuccess: (_data, vars) => invalidate(vars.clusterId),
  });
}

/** Delete several clusters at once (bulk action bar). */
export function useBulkDeleteClusters(tenantId: string) {
  const invalidate = useClusterInvalidation(tenantId);
  return useMutation({
    mutationFn: (clusterIds: string[]) =>
      serviceClusterService.bulkDelete(tenantId, clusterIds),
    onSuccess: () => invalidate(),
  });
}

/** Run AI Suggest across several clusters in one call. */
export function useBulkSuggestClusters(tenantId: string) {
  const queryClient = useQueryClient();
  const invalidate = useClusterInvalidation(tenantId);
  return useMutation({
    mutationFn: ({ clusterIds, limit }: { clusterIds: string[]; limit?: number }) =>
      serviceClusterService.bulkSuggest(tenantId, clusterIds, limit),
    onSuccess: (data) => {
      invalidate();
      // Refresh any open cluster's keyword list too.
      for (const r of data.results) {
        queryClient.invalidateQueries({ queryKey: serviceClusterQueryKeys.detail(r.cluster_id) });
      }
    },
  });
}
